import React, { FunctionComponent, MouseEvent, useEffect, useState } from 'react';
import { sortBy } from 'lodash';
import { Skeleton } from '@material-ui/lab';
import { Box, Button, DialogContent, DialogTitle, Grid, Typography, makeStyles } from '@material-ui/core';
import AutoCompleteSelect from 'src/components/AutoCompleteSelect';
import TableEmptyState from 'src/components/TableView/TableEmptyState';
import { License, DropDownField } from 'src/store/modules/application/types';

type LinkedAppointmentLicensesProps = {
    personLicenses: License[];
    personLicensesLoadingStatus: boolean;
    handleAddLicensesModal: Function;
    addLicensesForAppointments: Function;
    getFilteredLicensesForAppointments: Function;
    appointmentLicenses: License[];
};

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        color: ' #484848;',
        marginRight: theme.spacing(2),
    },
    headerLabel: {
        fontSize: 20,
    },
    spacer: {
        flexGrow: 1,
    },

    modalBtn: {
        marginLeft: 20,
    },
    btnContainer: {
        marginTop: 25,
    },
    checkboxLabel: {
        color: '#484848',
        fontSize: 16,
    },
}));

const LinkedAppointmentLicenses: FunctionComponent<LinkedAppointmentLicensesProps> = (props) => {
    const classes = useStyles();
    const {
        personLicenses,
        handleAddLicensesModal,
        addLicensesForAppointments,
        personLicensesLoadingStatus,
        getFilteredLicensesForAppointments,
        appointmentLicenses,
    } = props;

    const Loader = (
        <Box display="flex">
            <Skeleton width="5%" style={{ marginRight: 10 }} />
            <Skeleton width="50%" />
        </Box>
    );
    useEffect(() => {
        getFilteredLicensesForAppointments();
    }, [getFilteredLicensesForAppointments]);

    useEffect(() => {
        const licenseNumbers = appointmentLicenses.map((license: License) => license.id);
        const filteredPersonLicenses = personLicenses.filter(
            (personLicense) => !licenseNumbers.includes(personLicense.id),
        );
        setAvailableLicenses(filteredPersonLicenses);
    }, [personLicenses, appointmentLicenses]);

    const [licenses, setLicenses] = useState<DropDownField[]>([]);
    const [availableLicenses, setAvailableLicenses] = useState<License[]>();
    const handleChangeLicense = (data: DropDownField[]) => {
        setLicenses(data);
    };
    return (
        <>
            <DialogTitle className={classes.dialogTitle} disableTypography>
                <Typography variant="h6" className={classes.headerLabel}>
                    Add Licenses
                </Typography>
                <div className={classes.spacer} />
            </DialogTitle>
            <DialogContent>
                {personLicensesLoadingStatus ? (
                    <>
                        {Loader}
                        {Loader}
                    </>
                ) : availableLicenses?.length ? (
                    <AutoCompleteSelect
                        size={'small'}
                        value={licenses}
                        inputSize={'small'}
                        variant={'outlined'}
                        isMultipleValue={true}
                        id="add-licenses-select"
                        disableCloseOnSelect={true}
                        onChange={handleChangeLicense}
                        data={
                            Array.isArray(availableLicenses)
                                ? sortBy(availableLicenses, 'licenseNumber').map((license) => ({
                                      label:
                                          `${license.licenseNumber ? license.licenseNumber : ''}` +
                                          (license.additionalData?.rideName
                                              ? ` - ${license.additionalData?.rideName}`
                                              : ''),
                                      value: license.id,
                                  }))
                                : []
                        }
                    />
                ) : (
                    <TableEmptyState
                        title=""
                        message="There are no available licenses to link to this appointment."
                    ></TableEmptyState>
                )}
            </DialogContent>
            <Grid className={classes.btnContainer} sm={12} md={12} lg={12} item>
                <Box display="flex" justifyContent="flex-end">
                    <Button
                        onClick={(e: MouseEvent<HTMLElement>) => {
                            handleAddLicensesModal(false, e);
                        }}
                        className={classes.modalBtn}
                    >
                        CANCEL
                    </Button>
                    <Button
                        className={classes.modalBtn}
                        onClick={(e: MouseEvent<HTMLElement>) => {
                            e.stopPropagation();
                            const selectedLicenses = availableLicenses?.filter((license) =>
                                licenses.some((selectedLicense) => license.id === selectedLicense.value),
                            );
                            handleAddLicensesModal(false);
                            addLicensesForAppointments(
                                selectedLicenses?.map((license) => {
                                    return {
                                        id: license.id,
                                        licenseNumber: license.licenseNumber,
                                        type: {
                                            id: license.type.id,
                                            name: license.type.name,
                                        },
                                        status: license.status,
                                        issueDate: license.issueDate,
                                        expirationDate: license.expirationDate,
                                    };
                                }),
                            );
                        }}
                        variant="contained"
                        color="primary"
                        disabled={!licenses.length}
                    >
                        ADD
                    </Button>
                </Box>
            </Grid>
        </>
    );
};

export default LinkedAppointmentLicenses;
